import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { i18n } from 'next-i18next';
import toast from 'react-hot-toast';

import { AppThunk } from './index';
import { RootState } from './rootReducers';
import {
  postCreateMaterial,
  postDeleteMaterial,
  postPageQueryAvatarStyle,
  postPollPreviewAvatar,
  postPreviewAvatar,
  postQueryWebInpaintTimes,
  postRefinePrompt,
  postQueryPictureStyleDetail,
  postGetMaterialById,
  postGetWebPaintStyle,
} from '../api/material';
import { postGetUgcSettings } from '../api/meta';
import {
  IMAGE_MODEL_ID_MAP,
  DEFAULT_STYLE_ID_MAP,
  DEFAULT_STYLE_WEIGHT,
  DEFAULT_FACE_WEIGHT,
  CFG_SCALE_STEPS_MAP,
  getModelType,
} from '../constants/image';
import { PROMPT_TIPS as DEFAULT_PROMPT_TIPS } from '../constants/promptTips';
import { EAvatarTag } from '../types';
import {
  AvatarStyleElem,
  EImageModelType,
  EMaterialType,
  EPageQueryAvatarStyleScene,
  EPreviewAvatarScene,
  IImgElem,
  IPollPreviewAvatarReq,
  IPreviewAvatarReq,
  IQueryWebInpaintTimesResp,
  IStyleCategory,
  IWebPreviewExtra,
} from '../types/material';
import { IPromptTips } from '../types/meta';
import { isSensitiveContent } from '../utils';
import { combineAvatarFromMaterial } from '../utils/material';
// 目前上传到私有桶的图无法通过 url 请求到，只能存本地在用的时候本地获取
export const Global_Image_Memory: Record<string, File> = {};

interface IImgConfig extends IPreviewAvatarReq {
  trace_id?: string;
}

export interface ITaskResult {
  imgConfig: IImgConfig;
  imgIdList: number[];
}

export interface IImgElemWithInpaint extends IImgElem {
  inpaintImgIdList?: number[]; // 重绘历史的图片id列表
  materialId?: number; // 预览图片收藏成为素材后，有素材id
  isSensitive?: boolean; // 是否是敏感内容
  failed?: boolean; // 是否生成失败
}

interface IImgMap {
  [imgId: number]: IImgElemWithInpaint;
}

interface IImageState {
  shouldRequestModel?: boolean;
  imgConfig: IImgConfig;
  modelType: EImageModelType;
  isGenerating: boolean; // 是否正在生成图片
  taskResult: ITaskResult[];
  imgMap: IImgMap;
  inpaintTimes: IQueryWebInpaintTimesResp;
  avatarStyle: {
    avatarStyleList: AvatarStyleElem[];
    categoryList: IStyleCategory[];
    hasMore: boolean;
    offset: number;
    currCategoryId?: number | null;
    currStyleName?: string;
    isLoading: boolean;
  };
  promptTips: IPromptTips[];
  inpaintItem: {
    imgElemIdx: number;
    taskIdx: number;
    show: boolean;
    inpainting: boolean;
  };
  cropingImgUrl: string;
  styleNameMap: Record<number, { name: string; picUrl?: string }>; // Add this new property
  modelAvatarStyleMap: Record<
    string,
    {
      categoryIdStyleMap: Record<string | number, { avatarStyleList: AvatarStyleElem[] }>;
      categoryList: IStyleCategory[];
      currCategoryId: number | null;
      currStyleName: string | null;
    }
  >;
}

const defaultWebPreviewExtra: IWebPreviewExtra = {
  cfg_scale: CFG_SCALE_STEPS_MAP[EImageModelType.Common].mid, // 默认 medium
  img_model_id: IMAGE_MODEL_ID_MAP[EImageModelType.Common],
  style_weight: DEFAULT_STYLE_WEIGHT,
  face_weight: DEFAULT_FACE_WEIGHT,
};

const initialState: IImageState = {
  imgConfig: {
    web_preview_extra: {
      ...defaultWebPreviewExtra,
    },
  },
  modelType: EImageModelType.Common,
  isGenerating: false,
  taskResult: [],
  imgMap: {},
  inpaintTimes: {},
  avatarStyle: {
    avatarStyleList: [],
    categoryList: [],
    hasMore: true,
    offset: 0,
    currCategoryId: null,
    isLoading: false,
  },
  promptTips: DEFAULT_PROMPT_TIPS,
  inpaintItem: { imgElemIdx: 0, taskIdx: 0, show: false, inpainting: false },
  cropingImgUrl: '',
  styleNameMap: {},
  modelAvatarStyleMap: {},
};

const STYLE_LIST_PAGE_SIZE = 20;

// 获取画风列表
export const getAvatarStyleList = createAsyncThunk(
  'image/getAvatarStyleList',
  async ({ offset, category_id }: { offset: number; category_id?: number }, { getState }) => {
    const state = getState() as RootState;
    const model_id =
      state.image.imgConfig.web_preview_extra?.img_model_id ||
      IMAGE_MODEL_ID_MAP[EImageModelType.Common];

    const res = await postPageQueryAvatarStyle({
      offset,
      limit: STYLE_LIST_PAGE_SIZE,
      model_id,
      category_id,
      scene: EPageQueryAvatarStyleScene.WebList,
    });
    return {
      ...res,
      offset: offset + STYLE_LIST_PAGE_SIZE,
      category_id,
      isNew: offset === 0,
    };
  }
);

export const getPromptTips = createAsyncThunk('image/getPromptTips', async () => {
  return await postGetUgcSettings();
});

export const genAIWriterPrompt = createAsyncThunk(
  'image/genAIWriterPrompt',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const imgConfig = state.image.imgConfig;
    // 如果画风id列表不为空，则不用画风垫图
    const styleImgUrl =
      imgConfig.avatar_style_id_list && imgConfig.avatar_style_id_list.length > 0
        ? undefined
        : imgConfig.web_preview_extra?.style_img_url;
    return await postRefinePrompt({
      user_id: state.user.authData.userId,
      img_url: imgConfig.web_preview_extra?.face_img_url || styleImgUrl,
      prompt: imgConfig.prompt,
      position: imgConfig.position,
    });
  }
);

// Add this helper function
const initImgConfig = (baseConfig: IImgConfig, modelType: EImageModelType): IImgConfig => {
  // 初始化
  const avatarStyleIdList = baseConfig.avatar_style_id_list;
  const imgConfig = {
    ...baseConfig,
    web_preview_extra: {
      ...baseConfig.web_preview_extra,
    },
    avatar_style_id_list:
      avatarStyleIdList && avatarStyleIdList.length > 0
        ? avatarStyleIdList
        : [DEFAULT_STYLE_ID_MAP[modelType]],
    preview_avatar_scene: EPreviewAvatarScene.WebPreview,
    is_dsync: true,
  };

  // 修复服务端部分官方 style img url cdn 错误
  if (avatarStyleIdList && avatarStyleIdList.length > 0) {
    delete imgConfig.web_preview_extra?.style_img_url;
  }

  return imgConfig;
};

// 生成预览图片
export const generatePreviewImage = createAsyncThunk(
  'image/generatePreviewImage',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const modelType = state.image.modelType || EImageModelType.Common;
      const imgConfig = initImgConfig(state.image.imgConfig, modelType);
      const res = await postPreviewAvatar(imgConfig);
      // TODO: 调试需要临时加上 trace_id 展示
      const data = res.data;
      return {
        imgConfig: {
          ...imgConfig,
          trace_id: res.headers?.['trace-id'] || '',
        },
        imgList: data.img_list,
      };
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

// 重新生成预览图片
export const regeneratePreviewImage = createAsyncThunk(
  'image/regeneratePreviewImage',
  async (taskIndex: number, { getState }) => {
    const state = getState() as RootState;
    const taskResult = state.image.taskResult[taskIndex];
    if (!taskResult) {
      return {
        imgList: [],
        taskIndex,
      };
    }
    const imgConfig = {
      ...taskResult.imgConfig,
      preview_avatar_scene: EPreviewAvatarScene.WebPreview,
      is_dsync: true,
    };

    const res = await postPreviewAvatar(imgConfig);
    // TODO: 调试需要临时加上
    const data = res.data;
    return {
      imgList: data.img_list,
      taskIndex,
      trace_id: res.headers?.['trace-id'] || '',
    };
  }
);

// 重绘预览图片
export const inpaintImage = createAsyncThunk(
  'image/inpaintImage',
  async (
    {
      taskIdx,
      inpaintBaseImgUrl,
      originImgId,
      prompt,
    }: {
      taskIdx: number;
      inpaintBaseImgUrl: string;
      originImgId: number;
      prompt?: string;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const taskResult = state.image.taskResult[taskIdx];
      if (!taskResult) {
        return;
      }
      const imgConfig = {
        ...taskResult.imgConfig,
        base_img_url: inpaintBaseImgUrl,
        preview_avatar_scene: EPreviewAvatarScene.WebPreviewRegen,
        is_dsync: true,
      };
      if (prompt) {
        imgConfig.prompt = prompt;
      }
      const { data: res } = await postPreviewAvatar(imgConfig);
      return {
        imgList: res.img_list,
        taskIdx,
        originImgId,
      };
    } catch (e: any) {
      return rejectWithValue(e.status_msg);
    }
  }
);

// 轮询预览图片
export const pollPreviewImage = createAsyncThunk(
  'image/pollPreviewImage',
  async ({ data }: { data: IPollPreviewAvatarReq; type: PollQueueType }, { dispatch }) => {
    const response = await postPollPreviewAvatar(data);

    const pendingImgIds: number[] = [];
    const failedImgIds: number[] = [];

    if (response?.img_list && response.img_list.length > 0) {
      const tempImgList = [...data.img_id_list];
      response.img_list.forEach((img) => {
        if (img.img_id) {
          if (img.img_url) {
            tempImgList.splice(tempImgList.indexOf(img.img_id), 1);
          }
        }
      });
      // Remaining IDs are still pending
      pendingImgIds.push(...tempImgList);
    } else {
      // If no data returned, keep all IDs as pending
      pendingImgIds.push(...data.img_id_list);
    }

    if (pendingImgIds.length > 0) {
      const currentItem = pollQueue[0];
      currentItem.retryCount++;

      if (currentItem.retryCount >= MAX_RETRY_COUNT) {
        // Max retries reached - treat remaining as failed
        failedImgIds.push(...pendingImgIds);
        pollQueue.shift();
      } else {
        // Update queue with remaining pending IDs
        currentItem.imgIds = pendingImgIds;
        pollQueue = [currentItem, ...pollQueue.slice(1)];
      }
    } else {
      pollQueue.shift(); // Remove completed item
    }

    return {
      ...response,
      failedImgIds, // Include failed IDs in response
    };
  }
);

// 收藏图片（创建素材）
export const starImage = createAsyncThunk(
  'image/starImage',
  async ({ imgId, taskIdx }: { imgId: number; taskIdx: number }, { getState }) => {
    const state = getState() as RootState;
    const imgElem = state.image.imgMap[imgId]!;
    const taskResult = state.image.taskResult[taskIdx];
    const res = await postCreateMaterial({
      material_avatar_data: {
        avatar_info: {
          avatar_list: [
            {
              avatar_img_url: imgElem.img_url,
              avatar_tag: EAvatarTag.BaseImg,
              style_name: imgElem.style_name,
              keywords: imgElem.keywords,
              biz_extra: imgElem.biz_extra,
              avatar_style_id: imgElem.avatar_style_id,
              web_preview_extra: taskResult.imgConfig.web_preview_extra,
              img_desc: taskResult.imgConfig.prompt || '',
            },
            {
              avatar_img_url:
                taskResult.imgConfig.web_preview_extra?.face_img_url ||
                taskResult.imgConfig.web_preview_extra?.style_img_url ||
                '',
              avatar_tag: EAvatarTag.UserUploadImg,
              style_name: imgElem.style_name,
              keywords: imgElem.keywords,
              biz_extra: imgElem.biz_extra,
              avatar_style_id: imgElem.avatar_style_id,
              web_preview_extra: taskResult.imgConfig.web_preview_extra,
              img_desc: taskResult.imgConfig.prompt || '',
            },
          ],
        },
      },
      material_type: EMaterialType.MaterialType_Avatar,
    });
    return {
      imgId,
      materialId: res.material_id,
    };
  }
);

// 取消收藏图片（删除素材）
export const cancelStarImage = createAsyncThunk(
  'image/cancelStarImage',
  async ({ materialId, imgId }: { materialId: number; imgId: number }) => {
    await postDeleteMaterial({ material_id: materialId });
    return {
      imgId,
      materialId,
    };
  }
);

// 查询用户重绘次数
export const queryWebInpaintTimes = createAsyncThunk('image/queryWebInpaintTimes', async () => {
  return await postQueryWebInpaintTimes({});
});

// 查询画风列表
export const queryWebPaintStyle = createAsyncThunk(
  'image/queryWebPaintStyle',
  async ({ model_id }: { model_id: string }) => {
    return await postGetWebPaintStyle({ model_id });
  }
);

export enum PollQueueType {
  Generate = 'generate',
  Inpaint = 'inpaint',
}

export interface IPollQueueItem {
  imgIds: number[];
  retryCount: number;
  type: PollQueueType;
}

const MAX_RETRY_COUNT = 10;
const POLL_INTERVAL = 3000;

let pollQueue: IPollQueueItem[] = [];
let pollIntervalId: NodeJS.Timeout | null = null;

const startPolling = (): AppThunk => (dispatch) => {
  if (pollIntervalId) return;

  pollIntervalId = setInterval(() => {
    if (pollQueue.length === 0) {
      clearInterval(pollIntervalId!);
      pollIntervalId = null;
      return;
    }

    const currentItem = pollQueue[0];
    dispatch(
      pollPreviewImage({ data: { img_id_list: currentItem.imgIds }, type: currentItem.type })
    );
  }, POLL_INTERVAL);
};

export const addToPollQueue =
  (imgIds: number[], type: PollQueueType = PollQueueType.Generate): AppThunk =>
  (dispatch) => {
    pollQueue.push({ imgIds, retryCount: 0, type });
    dispatch(startPolling());
  };

// Add new thunk
export const queryStyleDetail = createAsyncThunk(
  'image/queryStyleDetail',
  async (styleId: number) => {
    const res = await postQueryPictureStyleDetail({ id: styleId });
    let displayStyleName = '';
    try {
      displayStyleName = res.style?.style_show_name || '';
    } catch {}
    return {
      styleId,
      styleName: res.style?.style_name || '',
      picUrl: res.style?.pic_url,
      displayStyleName,
    };
  }
);

export const queryModelIdByStyleId = createAsyncThunk(
  'image/queryModelIdByStyleId',
  async (styleId: number, { getState }) => {
    const {
      user: { authData },
    } = getState() as RootState;
    const res = await postQueryPictureStyleDetail(
      { id: styleId },
      {
        params: { authData },
      }
    );
    return res;
  }
);

export const getImageMaterialDetail = createAsyncThunk(
  'image/getImageMaterialDetail',
  async ({ materialId, action }: { materialId: number; action?: string }) => {
    const res = await postGetMaterialById({ material_id_list: [materialId] });
    return {
      materialId,
      materialList: res.material_infos || [],
      action,
    };
  }
);

export const imageSlice = createSlice({
  name: 'image',
  initialState,
  reducers: {
    setModelType: (state, action: PayloadAction<EImageModelType>) => {
      state.modelType = action.payload;
    },
    setImgConfig: (state, action: PayloadAction<Partial<IImgConfig>>) => {
      state.imgConfig = { ...state.imgConfig, ...action.payload };
    },
    setImgPreviewExtra: (state, action: PayloadAction<Partial<IWebPreviewExtra>>) => {
      if (!state.imgConfig.web_preview_extra) {
        state.imgConfig.web_preview_extra = { ...defaultWebPreviewExtra };
      }
      state.imgConfig.web_preview_extra = {
        ...state.imgConfig.web_preview_extra,
        ...action.payload,
      };
    },
    setIsGenerating: (state, action: PayloadAction<boolean>) => {
      state.isGenerating = action.payload;
    },
    setCurrCategoryId: (state, action: PayloadAction<number | undefined>) => {
      state.avatarStyle.currCategoryId = action.payload;
    },
    setCurrStyleName: (state, action: PayloadAction<string>) => {
      state.avatarStyle.currStyleName = action.payload;
    },
    selectInpaintImg: (
      state,
      action: PayloadAction<{ taskIdx: number; imgId: number; originSelectId: number }>
    ) => {
      const { taskIdx, imgId, originSelectId } = action.payload;
      const taskResult = state.taskResult[taskIdx];
      if (!taskResult) return;
      const originIdx = taskResult.imgIdList.indexOf(originSelectId);
      if (originIdx === -1) return;

      taskResult.imgIdList[originIdx] = imgId;

      state.imgMap[imgId].inpaintImgIdList = state.imgMap[originSelectId].inpaintImgIdList;
      delete state.imgMap[originSelectId].inpaintImgIdList;
    },
    setInpaintItem: (
      state,
      action: PayloadAction<Partial<{ imgElemIdx: number; taskIdx: number; show: boolean }>>
    ) => {
      state.inpaintItem = { ...state.inpaintItem, ...action.payload };
    },
    setCropingImgUrl: (state, action: PayloadAction<string>) => {
      state.cropingImgUrl = action.payload;
    },
    setIsStyleLoading: (state, action: PayloadAction<boolean>) => {
      state.avatarStyle.isLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generatePreviewImage.fulfilled, (state, action) => {
        state.taskResult.push({
          imgIdList: action.payload.imgList?.map((img: any) => img.img_id || 0) || [],
          imgConfig: {
            ...action.payload.imgConfig,
            web_preview_extra: { ...action.payload.imgConfig.web_preview_extra },
          },
        });
        action.payload.imgList?.forEach((img: any) => {
          if (img.img_id) {
            state.imgMap[img.img_id] = img;
            state.imgMap[img.img_id].inpaintImgIdList = [img.img_id];
          }
        });
      })
      .addCase(generatePreviewImage.rejected, (state, action) => {
        state.isGenerating = false;
        console.error('[image] generatePreviewImage rejected', action.error);
      });

    builder
      .addCase(regeneratePreviewImage.fulfilled, (state, action) => {
        const taskResult = state.taskResult[action.payload.taskIndex];
        if (taskResult) {
          // 重新生成的插在前面
          taskResult.imgIdList = [
            ...(action.payload.imgList?.map((img: any) => img.img_id || 0) || []),
            ...taskResult.imgIdList,
          ];
          taskResult.imgConfig.trace_id = action.payload.trace_id;
          action.payload.imgList?.forEach((img: any) => {
            if (img.img_id) {
              state.imgMap[img.img_id] = img;
              state.imgMap[img.img_id].inpaintImgIdList = [img.img_id];
            }
          });
        }
      })
      .addCase(regeneratePreviewImage.rejected, (state, action) => {
        state.isGenerating = false;
        console.error('[image] regeneratePreviewImage rejected', action.error);
      });

    builder
      .addCase(pollPreviewImage.fulfilled, (state, action) => {
        const type = action.meta.arg.type;
        let currentSelectIsSensitiveOrFailed = false;
        const { taskIdx, imgElemIdx } = state.inpaintItem;
        const taskResult = state.taskResult[taskIdx];
        let inpaintImgId = 0;
        if (taskResult) inpaintImgId = taskResult.imgIdList[imgElemIdx];

        if (action.payload.img_list?.length) {
          const hasSensitiveContent = action.payload.img_list.some((img) =>
            isSensitiveContent(img.img_url || '')
          );
          if (hasSensitiveContent) {
            toast.error(i18n?.t('sensitive_content') || '', { id: 'sensitive_content' });
          }

          // Handle successful images
          action.payload.img_list
            .filter((img) => !isSensitiveContent(img.img_url || ''))
            .forEach((img) => {
              if (img.img_id && img.img_url) {
                const oldImg = state.imgMap[img.img_id];
                state.imgMap[img.img_id] = mergeImg(oldImg, img);
              }
            });

          // Handle sensitive images
          action.payload.img_list
            .filter((img) => isSensitiveContent(img.img_url || ''))
            .forEach((img) => {
              if (img.img_id && img.img_url) {
                const oldImg = state.imgMap[img.img_id];
                state.imgMap[img.img_id] = mergeImg(oldImg, { isSensitive: true });
              }
              if (inpaintImgId && img.img_id === inpaintImgId) {
                currentSelectIsSensitiveOrFailed = true;
              }
            });
        }
        if (action.payload.failedImgIds?.length) {
          // Handle failed images
          action.payload.failedImgIds?.forEach((imgId) => {
            const oldImg = state.imgMap[imgId];
            state.imgMap[imgId] = mergeImg(oldImg, { failed: true });
            if (inpaintImgId && imgId === inpaintImgId) {
              currentSelectIsSensitiveOrFailed = true;
            }
          });
          toast.error(i18n?.t('generate_failed') || '');
        }

        if (type === PollQueueType.Generate && pollQueue.length === 0) {
          state.isGenerating = false;
        }
        if (type === PollQueueType.Inpaint && pollQueue.length === 0) {
          state.inpaintItem.inpainting = false;
        }
        if (!currentSelectIsSensitiveOrFailed) return;
        const imgElem = state.imgMap[inpaintImgId];
        if (!imgElem) return;
        if (!imgElem.inpaintImgIdList?.length) return;
        if (imgElem.inpaintImgIdList.length > 1) {
          const { taskIdx, imgElemIdx } = state.inpaintItem;
          const taskResult = state.taskResult[taskIdx];
          if (!taskResult) return;
          const imgId = imgElem.inpaintImgIdList[imgElem.inpaintImgIdList.length - 2];
          taskResult.imgIdList[imgElemIdx] = imgId;
          state.imgMap[imgId].inpaintImgIdList = state.imgMap[inpaintImgId].inpaintImgIdList;
          delete state.imgMap[inpaintImgId].inpaintImgIdList;
        }
      })
      .addCase(pollPreviewImage.rejected, (state, action) => {
        console.error('[image] pollPreviewImage rejected', action.error);
        // Remove the failed item from the queue
        pollQueue.shift();
        const type = action.meta.arg.type;
        if (type === PollQueueType.Generate && pollQueue.length === 0) {
          state.isGenerating = false;
        }
        if (type === PollQueueType.Inpaint && pollQueue.length === 0) {
          state.inpaintItem.inpainting = false;
        }
      });

    builder
      .addCase(inpaintImage.fulfilled, (state, action) => {
        if (action.payload?.imgList?.length) {
          // 减少重绘次数
          if (typeof state.inpaintTimes.remain_inpaint_times === 'number') {
            state.inpaintTimes.remain_inpaint_times -= 1;
          }
          action.payload.imgList.forEach((img: any) => {
            if (img.img_id) {
              const oldImg = state.imgMap[img.img_id];
              state.imgMap[img.img_id] = mergeImg(oldImg, img);
            }
          });
          // 重绘结果 id 加入当前图片的重绘历史
          const taskResult = state.taskResult[action.payload.taskIdx];
          if (!taskResult) return;
          const originImg = state.imgMap[action.payload.originImgId];
          if (!originImg) return;
          originImg.inpaintImgIdList?.push(
            ...(action.payload.imgList.map((img: any) => img.img_id || 0) || [])
          );
          state.inpaintItem.inpainting = true;

          // inpaint 后自动选中生成中的图片
          const { originImgId } = action.payload;
          if (!taskResult) return;
          const originIdx = taskResult.imgIdList.indexOf(originImgId);
          if (originIdx === -1) return;
          const imgId = action.payload.imgList[0].img_id!;

          taskResult.imgIdList[originIdx] = imgId;

          state.imgMap[imgId].inpaintImgIdList = state.imgMap[originImgId].inpaintImgIdList;
          delete state.imgMap[originImgId].inpaintImgIdList;
        }
      })
      .addCase(inpaintImage.rejected, (_, action) => {
        console.error('[image] inpaintImage rejected', action.error);
        if (typeof action.payload === 'string') {
          toast.error(action.payload);
        }
      });

    builder
      .addCase(starImage.fulfilled, (state, action) => {
        const imgId = action.payload.imgId;
        const imgElem = state.imgMap[imgId];
        if (imgElem) {
          // 标记图片为素材
          imgElem.materialId = action.payload.materialId;
        }
        toast.success(i18n?.t('collect_success') || '', { id: 'collect_success' });
      })
      .addCase(starImage.rejected, (_, action) => {
        console.error('[image] collectImage rejected', action.error);
      });

    builder
      .addCase(cancelStarImage.fulfilled, (state, action) => {
        const imgId = action.payload.imgId;
        const imgElem = state.imgMap[imgId];
        if (imgElem) {
          // 取消收藏图片
          imgElem.materialId = undefined;
        }
        toast.success(i18n?.t('uncollect_success') || '', { id: 'uncollect_success' });
      })
      .addCase(cancelStarImage.rejected, (_, action) => {
        console.error('[image] cancelStarImage rejected', action.error);
      });

    builder.addCase(queryWebInpaintTimes.fulfilled, (state, action) => {
      state.inpaintTimes = action.payload;
    });

    builder
      .addCase(getAvatarStyleList.fulfilled, (state, action) => {
        if (!action.payload.isNew) {
          state.avatarStyle.avatarStyleList.push(...(action.payload.avatar_style_list || []));
        } else {
          state.avatarStyle.avatarStyleList = [...(action.payload.avatar_style_list || [])];
        }
        state.avatarStyle.categoryList = [...(action.payload.category_list || [])];
        state.avatarStyle.hasMore = action.payload.has_more || true;
        state.avatarStyle.offset = action.payload.offset || 0;
        state.avatarStyle.isLoading = false;
      })
      .addCase(getAvatarStyleList.rejected, (state, action) => {
        state.avatarStyle.isLoading = false;
        console.error('[image] getAvatarStyleList rejected', action.error);
      });

    builder
      .addCase(genAIWriterPrompt.fulfilled, (state, action) => {
        state.imgConfig.prompt = action.payload.refine_prompt;
        state.isGenerating = false;
      })
      .addCase(genAIWriterPrompt.rejected, (state, action) => {
        state.isGenerating = false;
        console.error('[image] genAIWriterPrompt rejected', action.error);
      });

    builder
      .addCase(getPromptTips.fulfilled, (state, action) => {
        if (action.payload.data?.prompt_tips?.length) {
          state.promptTips = action.payload.data.prompt_tips;
        }
      })
      .addCase(getPromptTips.rejected, (_, action) => {
        console.error('[image] getPromptTips rejected', action.error);
      });

    builder.addCase(queryStyleDetail.fulfilled, (state, action) => {
      state.styleNameMap[action.payload.styleId] = {
        name: action.payload.displayStyleName || action.payload.styleName,
        picUrl: action.payload.picUrl,
      };
    });

    builder
      .addCase(getImageMaterialDetail.fulfilled, (state, action) => {
        const materialId = action.payload.materialId;
        const material = action.payload.materialList[materialId];
        if (!material) return;
        // 随机生成一个 imgId
        const imgId = Math.floor(Math.random() * 1000000);
        const avatarItem = combineAvatarFromMaterial(material.material_avatar_data!);
        if (!avatarItem) return;
        const avatarStyleId = avatarItem.avatar_style_id;
        const imgConfig = {
          prompt: avatarItem.img_desc || '',
          position: avatarItem.avatar_head_position || undefined,
          avatar_style_id_list: avatarStyleId ? [avatarStyleId] : [],
          web_preview_extra: avatarItem.web_preview_extra || { ...defaultWebPreviewExtra },
        };
        if (imgConfig.web_preview_extra.style_img_url) {
          imgConfig.avatar_style_id_list = [];
        }
        if (avatarItem.avatar_style_id && !avatarItem.web_preview_extra?.img_model_id) {
          state.shouldRequestModel = true;
        }

        const modelType = getModelType(avatarItem.web_preview_extra?.img_model_id);
        if (!imgConfig.position) delete imgConfig.position;
        state.imgMap[imgId] = {
          ...imgConfig,
          inpaintImgIdList: [imgId],
          materialId,
          img_url: avatarItem.avatar_img_url,
          img_id: imgId,
        };
        const currAction = action.payload.action;
        if (currAction === 'inpaint') {
          // 加入 task 列表
          state.taskResult = [
            {
              imgIdList: [imgId],
              imgConfig,
            },
          ];
          state.inpaintItem.show = true;
          state.inpaintItem.imgElemIdx = 0;
          state.inpaintItem.taskIdx = 0;
          state.imgConfig = {
            ...imgConfig,
          };
          if (modelType) state.modelType = modelType;
        } else if (currAction === 'recreate') {
          state.imgConfig = {
            ...imgConfig,
          };
          if (modelType) state.modelType = modelType;
        }
      })
      .addCase(getImageMaterialDetail.rejected, (_, action) => {
        console.error('[image] getImageLibraryDetail rejected', action.error);
      });
    builder.addCase(queryWebPaintStyle.fulfilled, (state, action) => {
      if (!state.modelAvatarStyleMap[action.meta.arg.model_id]) {
        state.modelAvatarStyleMap[action.meta.arg.model_id] = {
          categoryIdStyleMap: {},
          categoryList: [],
          currCategoryId: 0,
          currStyleName: null,
        };
      }
      Object.keys(action.payload.avatar_style_list || {}).forEach((categoryId) => {
        state.modelAvatarStyleMap[action.meta.arg.model_id].categoryIdStyleMap[categoryId] = {
          avatarStyleList: action.payload.avatar_style_list![Number(categoryId)],
        };
      });
      state.modelAvatarStyleMap[action.meta.arg.model_id].categoryList =
        action.payload.category_list || [];
    });
    builder.addCase(queryModelIdByStyleId.fulfilled, (state, action) => {
      state.shouldRequestModel = false;
      const modelName = action.payload.style?.model_name;
      const modelType = getModelType(modelName);
      if (modelType && state.imgConfig.web_preview_extra) {
        state.modelType = modelType;
        state.imgConfig.web_preview_extra!.img_model_id = modelName;
      }
    });
  },
});

function mergeImg(oldImg: IImgElemWithInpaint, newImg: IImgElemWithInpaint) {
  if (!oldImg) return newImg;
  const mergedImg = {
    ...oldImg,
    ...newImg,
  };
  if (oldImg.inpaintImgIdList) {
    mergedImg.inpaintImgIdList = [...oldImg.inpaintImgIdList];
  } else if (newImg.img_id) {
    mergedImg.inpaintImgIdList = [newImg.img_id];
  }
  return mergedImg;
}

const { actions } = imageSlice;
export const {
  setModelType,
  setImgConfig,
  setImgPreviewExtra,
  setIsGenerating,
  selectInpaintImg,
  setInpaintItem,
  setCurrCategoryId,
  setCropingImgUrl,
  setCurrStyleName,
  setIsStyleLoading,
} = actions;
